import * as Sentry from '@sentry/browser'
import axios from 'axios'
import { API_BASE_URL, BASE_URL, X_API_KEY } from './api'
import CryptoJS from 'crypto-js'
import { v4 as uuidv4 } from 'uuid'
import CookieManager from '../src/lib/CookiesManager'

const DEVICE_ID_KEY = 'device_id'
const CREDENTIALS_KEY = 'secure_credentials'
const TOKEN_EXPIRY_DAYS = 30

interface SecureCredentials {
  email: string
  deviceId: string
  expiresAt: number
}

interface LoginCredentials {
  email: string
  password: string
  rememberMe?: boolean
}

interface UserData {
  token: string | { value: string; expiration: string; isadmin: string }
  refresh_token: string
  id: string
  nickname: string
  email: string
  birthday: string
  address: {
    city: string
    zip_code: string
  }
}

interface LoginResponse {
  success?: string
  error?: string
  user?: UserData
}

const AuthService = {
  getDeviceId: (): string => {
    let deviceId = localStorage.getItem(DEVICE_ID_KEY)
    if (!deviceId) {
      deviceId = uuidv4()
      localStorage.setItem(DEVICE_ID_KEY, deviceId)
    }
    return deviceId
  },

  encryptData: (data: SecureCredentials): string => {
    const deviceId = AuthService.getDeviceId()
    const key = CryptoJS.SHA256(deviceId).toString()
    return CryptoJS.AES.encrypt(JSON.stringify(data), key).toString()
  },

  decryptData: (encryptedData: string): SecureCredentials | null => {
    try {
      const deviceId = AuthService.getDeviceId()
      const key = CryptoJS.SHA256(deviceId).toString()
      const bytes = CryptoJS.AES.decrypt(encryptedData, key)
      return JSON.parse(bytes.toString(CryptoJS.enc.Utf8))
    } catch (error) {
      Sentry.captureException(error, {
        extra: { action: 'decryptData' },
        tags: { type: 'crypto_error' },
      })
      return null
    }
  },

  saveSecureCredentials: (email: string) => {
    const credentials: SecureCredentials = {
      email,
      deviceId: AuthService.getDeviceId(),
      expiresAt: Date.now() + TOKEN_EXPIRY_DAYS * 24 * 60 * 60 * 1000,
    }
    const encryptedData = AuthService.encryptData(credentials)
    localStorage.setItem(CREDENTIALS_KEY, encryptedData)
  },

  loadSecureCredentials: (): string | null => {
    const encryptedData = localStorage.getItem(CREDENTIALS_KEY)
    if (!encryptedData) return null

    const credentials = AuthService.decryptData(encryptedData)
    if (!credentials) return null

    if (Date.now() > credentials.expiresAt) {
      AuthService.clearSecureCredentials()
      return null
    }

    if (credentials.deviceId !== AuthService.getDeviceId()) {
      AuthService.clearSecureCredentials()
      return null
    }

    return credentials.email
  },

  clearSecureCredentials: () => {
    localStorage.removeItem(CREDENTIALS_KEY)
  },

  login: async ({
    email,
    password,
    rememberMe,
  }: LoginCredentials): Promise<LoginResponse> => {
    try {
      const response = await fetch(`${BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify({ email, password, rememberMe }),
      })

      const data = await response.json()

      if (!response.ok) {
        if (response.status === 404) {
          return { error: "Ce compte n'existe pas." }
        } else if (response.status === 400) {
          return {
            error: data.error || 'Requête invalide. Vérifiez vos informations.',
          }
        } else if (response.status === 401) {
          return {
            error: 'Email ou mot de passe incorrect.',
          }
        }
        return {
          error: data.error || 'Une erreur est survenue lors de la connexion',
        }
      }

      AuthService.setUserData(data)
      if (!CookieManager.isLoginHintHidden()) {
        CookieManager.setLoginHintHidden()
      }

      return {
        success: `Bienvenue ${data.nickname}! Redirection en cours ...`,
        user: data,
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'login',
          email,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'auth_error',
          method: 'login',
        },
      })
      return { error: 'Une erreur est survenue lors de la connexion' }
    }
  },

  resetPassword: async (
    token: string,
    password: string,
  ): Promise<{ success?: string; error?: string }> => {
    try {
      const response = await fetch(`${BASE_URL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': X_API_KEY,
        },
        body: JSON.stringify({
          token,
          password,
          password_confirmation: password,
        }),
        credentials: 'include',
      })

      const text = await response.text()

      try {
        if (!text) {
          throw new Error('Empty response from server')
        }

        const data = JSON.parse(text)

        if (!response.ok) {
          throw new Error(data.error || `Server error: ${response.status}`)
        }

        return {
          success:
            'Mot de passe réinitialisé avec succès. Vous pouvez maintenant vous connecter avec votre nouveau mot de passe.',
        }
      } catch (parseError) {
        Sentry.captureException(parseError, {
          extra: {
            action: 'resetPassword_parse',
            responseText: text,
            responseStatus: response.status,
            responseHeaders: Object.fromEntries(response.headers.entries()),
            timestamp: new Date().toISOString(),
          },
        })
        throw new Error('Invalid server response')
      }
    } catch (error) {
      console.error('Reset password error:', error)
      Sentry.captureException(error, {
        extra: {
          action: 'resetPassword',
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'auth_error',
          method: 'resetPassword',
        },
      })
      return {
        error:
          (error as Error).message ||
          'La réinitialisation du mot de passe a échoué',
      }
    }
  },

  setUserData: (data: UserData): void => {
    const authData = {
      token: typeof data.token === 'object' ? data.token.value : data.token,
      refreshToken: data.refresh_token,
      expiration: typeof data.token === 'object' ? data.token.expiration : '',
      userId: data.id,
      email: data.email,
    }
    CookieManager.setAuth(authData)
  },

  logout: (): void => {
    CookieManager.clearAuth()
  },

  getToken: (): string | null => {
    return CookieManager.getToken()
  },

  getRefreshToken: (): string | null => {
    return CookieManager.getRefreshToken()
  },

  isAuthenticated: (): boolean => {
    return CookieManager.isAuthenticated()
  },

  refreshToken: async () => {
    try {
      const refreshToken = CookieManager.getRefreshToken()
      const userId = CookieManager.getUserId()

      if (!refreshToken || !userId) {
        throw new Error('No refresh token available')
      }

      const response = await axios.post(
        `${BASE_URL}/auth/refresh`,
        {
          refresh_token: refreshToken,
          user_id: userId,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${refreshToken}`,
            'x-api-key': X_API_KEY,
          },
        },
      )

      if (response.data && response.data.access_token) {
        const { access_token, refresh_token } = response.data

        CookieManager.updateTokens(
          access_token.value,
          refresh_token,
          access_token.expiration,
        )

        return access_token.value
      } else {
        throw new Error('Invalid refresh token response')
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'refreshToken',
          userId: CookieManager.getUserId(),
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'auth_error',
          method: 'refreshToken',
        },
      })

      if (axios.isAxiosError(error) && error.response?.status === 401) {
        AuthService.logout()
        window.location.href = '/login'
      }

      throw error
    }
  },

  updateUserInfo: async () => {
    const userId = CookieManager.getUserId()
    const token = CookieManager.getToken()

    if (!userId || !token) {
      throw new Error('User ID or token not found')
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-api-key': X_API_KEY },
      })

      const { refreshToken, refreshTokenExpiresAt } = response.data.user

      CookieManager.updateTokens(token, refreshToken, refreshTokenExpiresAt)

      return response.data.user
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          action: 'updateUserInfo',
          userId,
          timestamp: new Date().toISOString(),
        },
        tags: {
          type: 'auth_error',
          method: 'updateUserInfo',
        },
      })
      throw error
    }
  },

  checkTokenExpiration: async () => {
    const auth = CookieManager.getAuth()
    if (!auth || !auth.expiration) return false

    const expirationDate = new Date(auth.expiration)
    const now = new Date()
    const timeUntilExpiration = expirationDate.getTime() - now.getTime()
    const fiveMinutes = 240 * 60 * 1000

    if (timeUntilExpiration < fiveMinutes) {
      try {
        await AuthService.refreshToken()
        return true
      } catch (error) {
        Sentry.captureException(error, {
          extra: {
            action: 'checkTokenExpiration',
            timeUntilExpiration,
            timestamp: new Date().toISOString(),
          },
          tags: {
            type: 'auth_error',
            method: 'checkTokenExpiration',
          },
        })
        return false
      }
    }

    return true
  },
}

export default AuthService
